
import { useState, useCallback } from "react";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

export interface InlineFieldProps {
  label: string;
  value?: string | null;
  /** Called with the new trimmed value (empty string means "clear the field") */
  onSave: (value: string) => void;
  icon?: React.ReactNode;
  emptyText?: string;
  multiline?: boolean;
  /** For date inputs, pass value pre-formatted as YYYY-MM-DD */
  type?: "text" | "email" | "tel" | "date" | "url";
  className?: string;
}

export function InlineField({
  label,
  value,
  onSave,
  icon,
  emptyText,
  multiline,
  type = "text",
  className,
}: InlineFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = useCallback(() => {
    setDraft(value ?? "");
    setEditing(true);
  }, [value]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== (value ?? "")) {
      onSave(trimmed);
    }
  }, [draft, value, onSave]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      } else if (e.key === "Enter" && !multiline) {
        e.preventDefault();
        commit();
      } else if (e.key === "Enter" && multiline && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commit();
      }
    },
    [multiline, commit, cancel]
  );

  return (
    <div className={cn("group/field flex items-start gap-2.5", className)}>
      {icon && (
        <div className="mt-0.5 shrink-0 text-muted-foreground">{icon}</div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{label}</p>

        {editing ? (
          multiline ? (
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={commit}
              rows={3}
              placeholder={emptyText}
              className="w-full text-sm resize-none rounded-md border border-input bg-background px-2 py-1.5 outline-none focus:ring-1 focus:ring-ring transition-shadow"
            />
          ) : (
            <input
              autoFocus
              type={type}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={commit}
              placeholder={emptyText}
              className="w-full text-sm rounded-md border border-input bg-background px-2 py-1 outline-none focus:ring-1 focus:ring-ring transition-shadow"
            />
          )
        ) : (
          <button
            type="button"
            onClick={startEdit}
            className="group/val flex items-start gap-1.5 w-full text-left cursor-text rounded-sm hover:bg-accent/40 -mx-1 px-1 py-0.5 transition-colors"
          >
            {value ? (
              <span className="text-sm flex-1 break-words whitespace-pre-wrap">
                {value}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground/50 italic flex-1">
                {emptyText ?? `Add ${label.toLowerCase()}…`}
              </span>
            )}
            <Pencil className="h-3 w-3 shrink-0 mt-0.5 text-muted-foreground/30 md:text-transparent md:group-hover/field:text-muted-foreground/40 transition-colors" />
          </button>
        )}
      </div>
    </div>
  );
}
